import React, { useEffect, useState } from 'react';
import { Cpu, MemoryStick, HardDrive, Activity, Database, Loader2, FileArchive, CalendarClock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Btn, Card } from '@/components/ui-ios';

/**
 * SystemStatsCard - SuperAdmin dashboard widget showing live server metrics.
 *
 * Complements the SystemStatsAlert banner: the banner only appears under
 * load, this card is always visible (for SA) so a healthy server can be
 * verified at a glance. Polls every 60s, in sync with the banner cadence.
 */

const POLL_INTERVAL_MS = 60_000;

function Bar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  let tone = 'bg-emerald-500';
  if (pct >= 90) tone = 'bg-rose-600';
  else if (pct >= 75) tone = 'bg-amber-500';
  return (
    <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden">
      <div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Metric({ icon: Icon, label, value, suffix = '%', extra, testid }) {
  const pct = Number(value) || 0;
  const color = pct >= 90 ? 'text-rose-700' : pct >= 75 ? 'text-amber-700' : 'text-zinc-900';
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)] uppercase tracking-wide font-medium">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`text-2xl font-semibold tabular-nums ${color}`}>
        {value == null ? '—' : `${pct.toFixed(1)}${suffix}`}
      </div>
      <Bar value={pct} />
      {extra && <div className="text-[11px] text-[color:var(--text-secondary)]">{extra}</div>}
    </div>
  );
}

export default function SystemStatsCard() {
  const [stats, setStats] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [lastCompression, setLastCompression] = useState(null);
  const [retention, setRetention] = useState(null);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get('/admin/system-stats');
        if (!alive) return;
        setStats(data);
        setUpdatedAt(new Date());
      } catch (e) {
        // best-effort; banner already handles long-term failures.
      }
    };
    const loadRetention = async () => {
      try {
        const { data } = await api.get('/admin/retention-info');
        if (!alive) return;
        setRetention(data);
      } catch (e) {
        // non-fatal
      }
    };
    tick();
    loadRetention();
    const id = setInterval(() => { tick(); loadRetention(); }, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const refreshRetention = async () => {
    try {
      const { data } = await api.get('/admin/retention-info');
      setRetention(data);
    } catch (e) { /* noop */ }
  };

  const onPurge = async () => {
    const eligible = retention?.eligible_count || 0;
    const msg = eligible > 0
      ? `Procedo con la rimozione di foto e firme di ${eligible} raccomandata/e consegnate da oltre ${retention?.retention_days || 365} giorni?\n\n` +
        '✓ I metadati legali (data consegna, firmatario, GPS, esito) restano salvati per sempre.\n' +
        '✗ Foto e firme verranno eliminate definitivamente per liberare spazio.'
      : 'Nessuna raccomandata ha attualmente media da purgare. Vuoi comunque eseguire la scansione completa?';
    if (!window.confirm(msg)) return;
    setPurging(true);
    try {
      const { data } = await api.post('/admin/purge-media');
      const mb = (data?.mb_freed ?? 0).toFixed(2);
      toast.success(
        `Purga completata: ${data?.raccomandate_purged || 0} raccomandate, ${mb} MB liberati.`,
      );
      await refreshRetention();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore durante la purga';
      toast.error(detail);
    } finally {
      setPurging(false);
    }
  };

  const onRecompress = async () => {
    // Belt-and-braces confirm: the op rewrites image fields in-place and
    // can take a few seconds on larger datasets — surface the intent.
    const ok = window.confirm(
      'Avvia la compressione di tutte le foto e firme già archiviate?\n\n' +
      'L\'operazione è sicura (idempotente) e libera spazio disco sul database. ' +
      'Su molte raccomandate può richiedere qualche minuto.',
    );
    if (!ok) return;
    setCompressing(true);
    try {
      const { data } = await api.post('/admin/recompress-storage');
      setLastCompression(data);
      const saved = (data?.mb_saved ?? 0).toFixed(2);
      const att = data?.attachments_recompressed || 0;
      const sig = data?.signatures_recompressed || 0;
      toast.success(
        `Compressione completata: ${saved} MB liberati (${att} foto + ${sig} firme).`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Errore durante la compressione';
      toast.error(msg);
    } finally {
      setCompressing(false);
    }
  };

  return (
    <Card className="p-4 sm:p-5" data-testid="sa-system-stats-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Stato del server</h2>
          <p className="text-xs text-[color:var(--text-secondary)]">
            CPU, memoria e disco in tempo reale del container.
          </p>
        </div>
        <div className="text-[11px] text-[color:var(--text-secondary)] text-right">
          {updatedAt ? `Aggiornato ${updatedAt.toLocaleTimeString('it-IT')}` : 'Caricamento…'}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Metric
          icon={Cpu}
          label="CPU"
          value={stats?.cpu_percent}
          extra={
            stats?.cpu_count
              ? `${stats.cpu_count} core${stats.cpu_count === 1 ? '' : 's'}${stats.load_avg ? ` · load ${stats.load_avg.join(' / ')}` : ''}`
              : ''
          }
          testid="sa-stats-card-cpu"
        />
        <Metric
          icon={MemoryStick}
          label="Memoria"
          value={stats?.mem_percent}
          extra={
            stats?.mem_total_mb
              ? `${Math.round(stats.mem_used_mb).toLocaleString('it-IT')} / ${Math.round(stats.mem_total_mb).toLocaleString('it-IT')} MB`
              : ''
          }
          testid="sa-stats-card-ram"
        />
        <Metric
          icon={HardDrive}
          label="Disco"
          value={stats?.disk_percent}
          extra={
            stats?.disk_total_gb
              ? `${stats.disk_free_gb} GB liberi / ${stats.disk_total_gb} GB`
              : ''
          }
          testid="sa-stats-card-disk"
        />
      </div>
      <div className="mt-4 pt-3 border-t border-[color:var(--hairline)] flex items-center gap-4 flex-wrap text-xs text-[color:var(--text-secondary)]">
        <span className="inline-flex items-center gap-1.5" data-testid="sa-stats-card-mongo">
          <Database className="h-3.5 w-3.5" />
          MongoDB:{' '}
          <span className={stats?.mongo_ok ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>
            {stats?.mongo_ok ? `OK · ${stats.mongo_ping_ms} ms` : 'KO'}
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5" />
          Stato:{' '}
          <span
            className={
              stats?.level === 'crit'
                ? 'text-rose-700 font-semibold'
                : stats?.level === 'warn'
                ? 'text-amber-700 font-semibold'
                : 'text-emerald-700 font-semibold'
            }
            data-testid="sa-stats-card-level"
          >
            {stats?.level === 'crit' ? 'Critico' : stats?.level === 'warn' ? 'Carico elevato' : 'Normale'}
          </span>
        </span>
      </div>

      {/* Storage optimisation panel — manual trigger for the recompression
          migration that shrinks foto + firme già archiviate. Idempotent, so
          the SA can re-run it any time. */}
      <div className="mt-4 pt-4 border-t border-[color:var(--hairline)]">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileArchive className="h-4 w-4" /> Ottimizzazione storage
            </div>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1 leading-relaxed">
              Ricomprime tutte le foto e firme già caricate (JPEG q=75, max 1600&nbsp;px).
              Operazione sicura e ripetibile: libera spazio sul database senza perdita di qualità visibile.
            </p>
            {lastCompression && (
              <div
                className="mt-2 text-[11px] text-[color:var(--text-secondary)] bg-[color:var(--surface-2)] rounded-lg p-2"
                data-testid="sa-recompress-last-result"
              >
                <span className="font-semibold text-emerald-700">
                  Ultima esecuzione: {lastCompression.mb_saved?.toFixed(2)} MB liberati
                </span>{' '}
                · {lastCompression.attachments_recompressed} foto, {lastCompression.signatures_recompressed} firme
                {lastCompression.errors > 0 && (
                  <span className="text-rose-700"> · {lastCompression.errors} errori</span>
                )}
              </div>
            )}
          </div>
          <Btn
            variant="outline"
            size="sm"
            onClick={onRecompress}
            disabled={compressing}
            testid="sa-recompress-button"
          >
            {compressing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Compressione…
              </>
            ) : (
              <>
                <FileArchive className="h-4 w-4" />
                Comprimi archivio
              </>
            )}
          </Btn>
        </div>
      </div>

      {/* Retention policy — automatic daily purge of foto/firme older than
          MEDIA_RETENTION_DAYS. Surfaces last-run info + manual trigger. */}
      <div className="mt-4 pt-4 border-t border-[color:var(--hairline)]">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CalendarClock className="h-4 w-4" />
              Retention foto & firme
              {retention?.retention_days && (
                <span className="ml-1 text-xs font-normal text-[color:var(--text-secondary)]">
                  ({retention.retention_days} giorni)
                </span>
              )}
            </div>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1 leading-relaxed">
              Le foto e firme delle raccomandate consegnate da oltre {retention?.retention_days || 365} giorni
              vengono eliminate <strong>automaticamente ogni giorno</strong>. I metadati legali (firmatario,
              data, GPS, esito) restano archiviati per sempre.
            </p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[color:var(--text-secondary)]">
              <span data-testid="sa-retention-eligible">
                Pronte per la purga:{' '}
                <span className={`font-semibold ${(retention?.eligible_count || 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {retention?.eligible_count ?? '—'}
                </span>
              </span>
              {retention?.last_run?.ran_at && (
                <span data-testid="sa-retention-last-run">
                  Ultima esecuzione automatica:{' '}
                  <span className="font-semibold text-zinc-800">
                    {new Date(retention.last_run.ran_at).toLocaleString('it-IT')}
                  </span>
                  {' · '}
                  {retention.last_run.raccomandate_purged} purgate,
                  {' '}
                  {(retention.last_run.bytes_freed / 1024 / 1024).toFixed(2)} MB liberati
                </span>
              )}
              {retention?.last_run?.error && (
                <span className="text-rose-700">
                  ⚠ Errore: {retention.last_run.error}
                </span>
              )}
            </div>
          </div>
          <Btn
            variant="outline"
            size="sm"
            onClick={onPurge}
            disabled={purging}
            testid="sa-purge-button"
          >
            {purging ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Purga in corso…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Purga ora
              </>
            )}
          </Btn>
        </div>
      </div>
    </Card>
  );
}
